import { useMemo, useState } from "react";
import { useLocation } from "wouter";
import {
  Plus,
  FolderKanban,
  Star,
  MoreHorizontal,
  Library,
  CheckSquare,
  Lightbulb,
  ArrowRight,
  ArrowUpDown,
  ChevronDown,
  Search,
  LayoutGrid,
  List,
  CalendarDays,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Thumb } from "@/components/Thumb";
import { EmptyState } from "@/components/EmptyState";
import { useItemsStore } from "@/lib/stores/itemsStore";
import { splitMeta, STATUS_DOT } from "@/lib/format";
import { STATUS_META } from "@/components/StatusBadge";
import { DEFAULT_PROJECT_ICON } from "@/lib/data/projects";

const TABS = [
  { key: "todos", label: "Todos" },
  { key: "activo", label: "Activos" },
  { key: "en-pausa", label: "En pausa" },
  { key: "archivado", label: "Archivados" },
];

const ProjectCard = ({ project, items, onOpenItem }) => {
  const Icon = project.icon || DEFAULT_PROJECT_ICON;
  const linked = project.itemIds.map((id) => items.find((i) => i.id === id)).filter(Boolean);

  return (
    <div className="rounded-lg border bg-card p-4 flex flex-col hover:border-teal-300/60 hover:shadow-sm transition-all" data-testid={`project-card-${project.id}`}>
      <div className="flex items-start gap-3">
        <div className={cn("w-11 h-11 rounded-lg grid place-items-center shrink-0", project.iconCls || "bg-accent text-primary")}>
          <Icon className="h-5 w-5" strokeWidth={1.75} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-serif text-lg leading-tight flex items-center gap-1.5">
            <span className="truncate">{project.name}</span>
            {project.starred && <Star className="h-3.5 w-3.5 text-amber-500 shrink-0" fill="currentColor" strokeWidth={0} />}
          </p>
          <p className="mt-0.5 text-[13px] text-muted-foreground line-clamp-2">{project.description}</p>
        </div>
        <button type="button" aria-label="Más" onClick={() => toast("Más acciones en la versión final.")} className="text-muted-foreground/60 hover:text-foreground">
          <MoreHorizontal className="h-4 w-4" strokeWidth={1.75} />
        </button>
      </div>

      <div className="mt-3.5 flex items-center gap-4 text-[13px] text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <Library className="h-3.5 w-3.5" strokeWidth={1.75} />
          <span className="font-semibold text-foreground tabular-nums">{project.itemCount}</span> contenidos
        </span>
        <span className="flex items-center gap-1.5">
          <CheckSquare className="h-3.5 w-3.5" strokeWidth={1.75} />
          <span className="font-semibold text-foreground tabular-nums">{project.taskCount}</span> tareas
        </span>
        <span className="flex items-center gap-1.5">
          <Lightbulb className="h-3.5 w-3.5" strokeWidth={1.75} />
          <span className="font-semibold text-foreground tabular-nums">{project.ideaCount}</span> ideas
        </span>
        <span className="ml-auto text-xs">
          Actualizado <span className="text-foreground">{project.updated}</span>
        </span>
      </div>

      <div className="my-3.5 h-px bg-border" />

      {project.tasks?.length ? (
        <div className="flex-1">
          <p className="text-xs font-semibold text-muted-foreground mb-2">Tareas pendientes</p>
          <div className="space-y-2">
            {project.tasks.map((t) => (
              <div key={t.id} className="flex items-center gap-2.5">
                <Checkbox aria-label={t.title} onCheckedChange={() => toast.success("Tarea completada.")} />
                <span className="flex-1 text-sm truncate">{t.title}</span>
                <span className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                  <CalendarDays className="h-3 w-3" strokeWidth={1.75} /> {t.due}
                </span>
              </div>
            ))}
          </div>
          <button type="button" onClick={() => toast(`Las ${project.taskCount} tareas, en la versión final.`)} className="mt-2.5 flex items-center gap-1 text-[13px] text-primary hover:underline">
            Ver todas las {project.taskCount} tareas <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} />
          </button>
        </div>
      ) : (
        <div className="flex-1">
          <p className="text-xs font-semibold text-muted-foreground mb-2">Últimos contenidos</p>
          <div className="space-y-2">
            {linked.slice(0, 3).map((i) => (
              <button key={i.id} type="button" onClick={() => onOpenItem(i.id)} className="w-full flex items-center gap-2.5 text-left hover:bg-secondary/60 rounded-md p-1 -m-1 transition-colors">
                <Thumb platform={i.platform} size="mini" />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium truncate">{i.title}</span>
                  <span className="block text-xs text-muted-foreground truncate">
                    {i.platform}
                    {splitMeta(i.meta).author ? ` · ${splitMeta(i.meta).author}` : ""}
                  </span>
                </span>
                <span
                  className={cn(
                    "flex items-center gap-1 text-[11px] font-medium shrink-0",
                    i.status === "pendiente" && "text-amber-700 dark:text-amber-400",
                    i.status === "importante" && "text-teal-700 dark:text-teal-400",
                    i.status === "aplicado" && "text-green-700 dark:text-green-400",
                    (i.status === "inbox" || i.status === "archivado") && "text-muted-foreground",
                  )}
                >
                  <span className={cn("w-1.5 h-1.5 rounded-full", STATUS_DOT[i.status])} />
                  {STATUS_META[i.status]?.label}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      <button type="button" onClick={() => toast("Vista de proyecto en la versión final.")} className="mt-3.5 flex items-center gap-1 text-[13px] text-primary font-medium hover:underline">
        Ver proyecto <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} />
      </button>
    </div>
  );
};

export default function Proyectos() {
  const { projects, items, addProject, select } = useItemsStore();
  const [, navigate] = useLocation();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [tab, setTab] = useState("todos");
  const [q, setQ] = useState("");

  const create = () => {
    const n = name.trim();
    if (!n) return;
    addProject(n);
    toast.success("Proyecto creado.");
    setName("");
    setOpen(false);
  };

  const openItem = (id) => {
    select(id);
    navigate("/biblioteca");
  };

  const visible = useMemo(() => {
    let list = projects;
    if (tab !== "todos") list = list.filter((p) => (p.estado || "activo") === tab);
    if (q.trim()) list = list.filter((p) => p.name.toLowerCase().includes(q.trim().toLowerCase()));
    return list;
  }, [projects, tab, q]);

  const countFor = (key) => (key === "todos" ? projects.length : projects.filter((p) => (p.estado || "activo") === key).length);

  return (
    <div className="h-full overflow-y-auto scrollbar-thin" data-testid="projects-page">
      <div className="px-4 md:px-8 py-6 md:py-8 max-w-[1400px] mx-auto space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="font-serif text-3xl tracking-tight">Proyectos</h1>
            <p className="mt-1.5 text-muted-foreground text-[15px]">
              Organiza tu conocimiento por áreas de trabajo. Todo lo que guardas puede vivir en varios proyectos.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="relative hidden lg:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar en tus proyectos…" className="pl-9 w-64" />
            </div>
            <Button onClick={() => setOpen(true)} data-testid="new-project-button">
              <Plus className="h-4 w-4" strokeWidth={2} /> Nuevo proyecto
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={cn(
                "rounded-full px-3 py-1.5 text-[13px] font-medium transition-colors",
                tab === t.key ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground",
              )}
            >
              {t.label}{" "}
              <span className={cn("tabular-nums", tab === t.key ? "" : "text-muted-foreground/70")}>{countFor(t.key)}</span>
            </button>
          ))}
          <span className="flex-1" />
          <button
            type="button"
            onClick={() => toast("Orden configurable en la versión final.")}
            className="hidden md:inline-flex items-center gap-1.5 rounded-md border bg-card px-2.5 py-1.5 text-[13px] text-muted-foreground hover:text-foreground"
          >
            <ArrowUpDown className="h-3.5 w-3.5" strokeWidth={1.75} /> Ordenar por: Actividad reciente
            <ChevronDown className="h-3.5 w-3.5 opacity-60" strokeWidth={1.75} />
          </button>
          <div className="hidden md:flex rounded-md border overflow-hidden">
            <span className="p-2 bg-accent text-accent-foreground">
              <LayoutGrid className="h-3.5 w-3.5" strokeWidth={1.75} />
            </span>
            <button type="button" onClick={() => toast("Vista de lista en la versión final.")} aria-label="Vista de lista" className="p-2 text-muted-foreground hover:bg-secondary border-l">
              <List className="h-3.5 w-3.5" strokeWidth={1.75} />
            </button>
          </div>
        </div>

        {visible.length === 0 ? (
          <div className="rounded-lg border bg-card">
            <EmptyState icon={FolderKanban} title="Agrupa contenidos por proyecto o área de trabajo." testId="projects-empty" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-4">
            {visible.map((p) => (
              <ProjectCard key={p.id} project={p} items={items} onOpenItem={openItem} />
            ))}
          </div>
        )}

        <div className="rounded-lg border bg-card px-4 py-3.5 flex items-center gap-3">
          <FolderKanban className="h-4 w-4 text-primary shrink-0" strokeWidth={1.75} />
          <p className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">Los proyectos te ayudan a mantener el foco.</span> Puedes añadir cualquier contenido a uno o varios proyectos desde la Biblioteca.
          </p>
          <button type="button" onClick={() => toast("Guía de proyectos en la versión final.")} className="ml-auto flex items-center gap-1 text-[13px] text-primary font-medium hover:underline shrink-0">
            Saber más sobre proyectos <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} />
          </button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-sm rounded-[14px]" data-testid="new-project-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Nuevo proyecto</DialogTitle>
            <DialogDescription className="sr-only">Crea un proyecto para agrupar contenidos.</DialogDescription>
          </DialogHeader>
          <Input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && create()}
            placeholder="Nombre del proyecto"
            data-testid="new-project-name-input"
          />
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={create} data-testid="new-project-create-button">
              Crear
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
